/**
 * ga4-events.js — GA4 custom event tracking for TechRental
 *
 * Fires custom events via gtag() (Google Channel) or dataLayer.push (GTM).
 * All events include product_name for per-model (S26/S25/S24) breakdown.
 *
 * Loaded by: layout/theme.liquid (site-wide)
 */
(function () {
  'use strict';

  var PII_PATTERNS = [
    /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/,
    /0\d{1,4}[\-\s]?\d{1,4}[\-\s]?\d{3,4}/,
    /\+?\d{10,15}/,
    /\d{3}[\-]?\d{4}/
  ];

  function sanitizeErrorMessage(msg) {
    if (!msg || typeof msg !== 'string') return 'unknown';
    for (var i = 0; i < PII_PATTERNS.length; i++) {
      if (PII_PATTERNS[i].test(msg)) return 'internal error';
    }
    return msg.substring(0, 200);
  }

  function sendEvent(eventName, params) {
    params = params || {};
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    } else if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    }
  }

  // カレンダーUIの版。改修の前後をタイムスタンプではなくイベント自身の値で
  // 比較するための印（Shopifyのアセットキャッシュにより、デプロイ後しばらく
  // 旧JSを実行する訪問者が混ざるため、日付境界だけでは切り分けられない）。
  // カレンダーのUXを変更したら必ずこの日付を更新すること。
  var CALENDAR_UI_VERSION = '2026-10-23';

  function sendCalendarEvent(eventName, params) {
    params = params || {};
    params.calendar_ui_version = CALENDAR_UI_VERSION;
    sendEvent(eventName, params);
  }

  function getProductName() {
    var el = document.querySelector('[data-product-title]');
    if (el) return el.dataset.productTitle;
    var h1 = document.querySelector('.product__title h1');
    if (h1) return h1.textContent.trim();
    return '';
  }

  // ── Remove → re-add tracking (sessionStorage) ──
  // Cart rows show product.title (.cart-item__name) and getProductName()
  // returns the same product.title on product pages, so titles match across
  // the remove/re-add pair. Storage failures (private mode) degrade to
  // is_readd never being true — acceptable.

  var READD_KEY = 'tr_removed_at';
  var READD_WINDOW_MS = 30 * 60 * 1000;

  function markRemoved(productTitle) {
    if (!productTitle) return;
    try {
      var map = JSON.parse(sessionStorage.getItem(READD_KEY) || '{}');
      map[productTitle] = Date.now();
      sessionStorage.setItem(READD_KEY, JSON.stringify(map));
    } catch (e) { /* storage unavailable */ }
  }

  // Seconds since this product was removed from the cart in this session,
  // or null if it wasn't (or the removal is older than the 30-min window).
  // Consumes the marker either way, so only the FIRST re-add of a product
  // pairs with its removal — later adds of the same product in the window
  // are new additions, not part of the adjustment loop.
  function secondsSinceRemoved(productTitle) {
    if (!productTitle) return null;
    try {
      var map = JSON.parse(sessionStorage.getItem(READD_KEY) || '{}');
      var ts = map[productTitle];
      if (!ts) return null;
      delete map[productTitle];
      sessionStorage.setItem(READD_KEY, JSON.stringify(map));
      var elapsed = Date.now() - ts;
      if (elapsed < 0 || elapsed > READD_WINDOW_MS) return null;
      return Math.round(elapsed / 1000);
    } catch (e) {
      return null;
    }
  }

  function getModelFamily(name) {
    if (!name) return 'unknown';
    var lower = name.toLowerCase();
    if (lower.indexOf('s26') !== -1) return 'S26';
    if (lower.indexOf('s25') !== -1) return 'S25';
    if (lower.indexOf('s24') !== -1) return 'S24';
    if (lower.indexOf('s23') !== -1) return 'S23';
    return 'other';
  }

  // ── Rental Calendar Events ──

  window.TRAnalytics = {
    planSelected: function (planName, price, days) {
      var product = getProductName();
      sendCalendarEvent('plan_selected', {
        product_name: product,
        model_family: getModelFamily(product),
        plan_name: planName,
        plan_price: price,
        plan_days: days
      });
    },

    extensionSelected: function (label, price, days) {
      var product = getProductName();
      sendCalendarEvent('extension_selected', {
        product_name: product,
        model_family: getModelFamily(product),
        extension_label: label,
        extension_price: price,
        extension_days: days
      });
    },

    dateSelected: function (startDate, endDate, totalDays, leadTimeDays, startDow) {
      var product = getProductName();
      sendCalendarEvent('date_selected', {
        product_name: product,
        model_family: getModelFamily(product),
        start_date: startDate,
        end_date: endDate,
        total_days: totalDays,
        lead_time_days: typeof leadTimeDays === 'number' ? leadTimeDays : null,
        start_dow: startDow || ''
      });
    },

    // Calendar modal opened (funnel entry: product view → calendar open).
    // trigger_location: which trigger was clicked ('inline' = the button in
    // the product info column; future triggers e.g. 'sticky' pass their own).
    calendarOpened: function (hasAvailability, triggerLocation) {
      var product = getProductName();
      sendCalendarEvent('calendar_opened', {
        product_name: product,
        model_family: getModelFamily(product),
        has_availability: !!hasAvailability,
        trigger_location: triggerLocation || 'inline'
      });
    },

    // Booking-button impression: the trigger scrolled into view, whether or
    // not it was clicked. Splits the pre-calendar drop-off into "never
    // reached the button" (no event) vs "saw it but didn't open" (this event
    // without a matching calendar_opened).
    calendarTriggerViewed: function (triggerLocation) {
      var product = getProductName();
      sendCalendarEvent('calendar_trigger_viewed', {
        product_name: product,
        model_family: getModelFamily(product),
        trigger_location: triggerLocation || 'inline',
        seconds_to_view: window.performance
          ? Math.round(performance.now() / 1000)
          : null
      });
    },

    // Price-table impression. Sessions with this event but no calendar_opened
    // are the "price checker" segment (came to look up the price, no intent
    // to book) — the candidate explanation for the post-7/27 open-rate drop.
    priceTableViewed: function () {
      var product = getProductName();
      sendCalendarEvent('price_table_viewed', {
        product_name: product,
        model_family: getModelFamily(product)
      });
    },

    // Calendar modal closed. reached_step = how far the user got before
    // closing ('browse' | 'date' | 'timeslot' | 'added'); the key drop-off
    // signal for finding where the widget loses people.
    calendarClosed: function (reachedStep, addedToCart, startDate, totalDays) {
      var product = getProductName();
      sendCalendarEvent('calendar_closed', {
        product_name: product,
        model_family: getModelFamily(product),
        reached_step: reachedStep,
        added_to_cart: !!addedToCart,
        start_date: startDate || '',
        total_days: totalDays || 0
      });
    },

    // Month navigation. months_from_now signals how far out the user looks
    // (high value = near-term availability is poor, or booking far ahead).
    monthNavigated: function (direction, month, monthsFromNow) {
      var product = getProductName();
      sendCalendarEvent('month_navigated', {
        product_name: product,
        model_family: getModelFamily(product),
        direction: direction,
        month: month,
        months_from_now: monthsFromNow
      });
    },

    // CTA pressed while the booking isn't ready — a dead/confusing click.
    // reason: 'no_date' (nothing visibly happens) | 'no_timeslot' (only
    // opens the time picker). The clearest "where do users get stuck" signal.
    // 日付選択済み・時間帯未選択でCTAを押した＝時間帯シートが正常に開く
    // 導線であって「詰まり」ではない。cta_blocked(no_timeslot) では実態と
    // 名前がズレるため専用イベントに分離（cta_blocked は本当に何も起きない
    // no_date / range_taken 用に残す）。
    progressiveCtaClicked: function () {
      var product = getProductName();
      sendCalendarEvent('progressive_cta_clicked', {
        product_name: product,
        model_family: getModelFamily(product),
        step: 'open_timeslot_sheet'
      });
    },

    ctaBlocked: function (reason) {
      var product = getProductName();
      sendCalendarEvent('cta_blocked', {
        product_name: product,
        model_family: getModelFamily(product),
        reason: reason
      });
    },

    timeslotSelected: function (timeslot) {
      var product = getProductName();
      sendCalendarEvent('timeslot_selected', {
        product_name: product,
        model_family: getModelFamily(product),
        timeslot: timeslot
      });
    },

    // total_inventory は availability API v2 (NOB-1290) でクライアントに
    // 渡らなくなったため送信しない。
    availabilityChecked: function (productSlug) {
      var product = getProductName() || productSlug;
      sendCalendarEvent('availability_checked', {
        product_name: product,
        model_family: getModelFamily(product)
      });
    },

    availabilityFailed: function (productSlug, errorMsg) {
      var product = getProductName() || productSlug;
      sendCalendarEvent('availability_failed', {
        product_name: product,
        model_family: getModelFamily(product),
        error_message: sanitizeErrorMessage(errorMsg)
      });
    },

    dateUnavailable: function (dateStr, reason) {
      var product = getProductName();
      sendCalendarEvent('date_unavailable', {
        product_name: product,
        model_family: getModelFamily(product),
        // 'attempted_start_date', not 'date': the generic name collides with
        // GA4's built-in `date` dimension in Data API queries (NOB-1245).
        attempted_start_date: dateStr,
        reason: reason
      });
    },

    // lead_time_days: 何日先の開始日をカートに入れたか。date_selected だけに
    // あると「先の日程を探した人が結局カートまで来たか」が追えないため、
    // カート側にも引き継ぐ（月送り改善の効果判定の主指標）。
    addToCartClicked: function (planName, price, startDate, endDate, leadTimeDays) {
      var product = getProductName();
      sendCalendarEvent('add_to_cart_clicked', {
        product_name: product,
        model_family: getModelFamily(product),
        plan_name: planName,
        value: price,
        currency: 'JPY',
        start_date: startDate,
        end_date: endDate,
        lead_time_days: leadTimeDays
      });
    },

    // is_readd / seconds_since_remove: whether this add follows a cart
    // removal of the same product in this session. Changing dates or plan
    // requires remove → re-add through the calendar, so a removal followed by
    // a re-add is an adjustment loop, not abandonment — this separates the
    // two readings of rising cart_item_removed counts.
    addToCartSuccess: function (planName, price, leadTimeDays) {
      var product = getProductName();
      var sinceRemove = secondsSinceRemoved(product);
      sendCalendarEvent('add_to_cart_success', {
        product_name: product,
        model_family: getModelFamily(product),
        plan_name: planName,
        value: price,
        currency: 'JPY',
        is_readd: sinceRemove !== null,
        seconds_since_remove: sinceRemove,
        lead_time_days: leadTimeDays
      });
    },

    addToCartFailed: function (planName, errorMsg) {
      var product = getProductName();
      sendCalendarEvent('add_to_cart_failed', {
        product_name: product,
        model_family: getModelFamily(product),
        plan_name: planName,
        error_message: sanitizeErrorMessage(errorMsg)
      });
    },

    // ── Cart / Checkout Events ──

    checkoutButtonClicked: function (cartTotal, itemCount, location) {
      sendEvent('checkout_button_clicked', {
        value: cartTotal,
        currency: 'JPY',
        item_count: itemCount,
        location: location
      });
    },

    cartItemRemoved: function (productTitle, variantTitle) {
      markRemoved(productTitle);
      sendEvent('cart_item_removed', {
        product_name: productTitle,
        variant_title: variantTitle
      });
    },

    // カート層が延長行と本体の日程を連動させた（cart-extension-sync.js・NOB-1488）。
    // 1つの出来事につき1回。action:
    //   'rollback' … 本体を延長なしに書き戻した（延長行が無い）
    //   'partial'  … 本体の一部だけ延長の料金がある。揃うまでチェックアウトを塞いだ
    //   'orphan'   … 親のいない延長行を外した
    //   'excess'   … 延長行の余った数量を減らした
    cartExtensionSynced: function (action, productTitle, extensionDays) {
      sendEvent('cart_extension_synced', {
        action: action,
        product_name: productTitle,
        extension_days: extensionDays
      });
    },

    // ── Product Page CTA Events ──

    lineConsultClicked: function (product) {
      sendEvent('line_consult_clicked', {
        product_name: product,
        model_family: getModelFamily(product),
        page_url: window.location.pathname
      });
    },

    // context: 'related_products'（既定・既存の関連商品クリック）|
    //          'rejection_chip'（カレンダー却下バナーの機種チップ・NOB-1123）
    alternativeClicked: function (fromProduct, toProduct, context) {
      sendEvent('alternative_clicked', {
        from_product: fromProduct,
        model_family: getModelFamily(fromProduct),
        to_product: toProduct,
        page_url: window.location.pathname,
        context: context || 'related_products'
      });
    },

    // ── 却下バナー（NOB-1123）──

    // カレンダーのグレー日タップで代替機種チップを表示した
    alternativeShown: function (reason, dateStr, altSlugs) {
      var product = getProductName();
      sendCalendarEvent('alternative_shown', {
        product_name: product,
        model_family: getModelFamily(product),
        reason: reason,
        // date_unavailable と同じ理由で GA4 組み込みの `date` を避ける
        attempted_start_date: dateStr,
        alternatives: altSlugs.join(',')
      });
    },

    // ── 早割（NOB-1616 / 親 NOB-1614）──
    // 早割のイベントは1つに集約し、action で段階を分ける（GA4 のカスタム
    // イベント枠を増やさない。ops 側 analytics_sync.py の GA4_EVENT_NAMES に
    // 'early_bird' を足して marketing.ga4_event_source_daily で引く）。
    //   action: 'shown'         … 早割ありの状態でカレンダーを開いた（凡例・面表示が見えた）
    //           'nudge_clicked' … 「M/D以降の開始なら◯%オフ」を押して対象日へ移った
    //           'date_selected' … 対象日を開始日に選んだ
    //           'add_to_cart'   … 対象日のままカート追加に成功した
    // min_lead_days / percent はその時点の設定値（ops が変えた後の効果比較用）。
    // lead_time_days は選んだ開始日までの日数（shown では null）。
    earlyBird: function (action, minLeadDays, percent, leadTimeDays) {
      var product = getProductName();
      sendCalendarEvent('early_bird', {
        product_name: product,
        model_family: getModelFamily(product),
        action: action,
        min_lead_days: minLeadDays,
        percent: percent,
        lead_time_days: typeof leadTimeDays === 'number' ? leadTimeDays : null
      });
    },

    // 却下バナーの機種以外のチップ（最短日ジャンプ・短縮プラン）のクリック。
    // 直後に発火する date_selected が「バナー経由の復帰」だと分かるようにする。
    // attempted_start_date は顧客が試した日（date_unavailable とJOIN可能）、
    // target_date はチップが適用する日。
    rejectionChipClicked: function (chip, attemptedDateStr, reason, targetDateStr) {
      var product = getProductName();
      sendCalendarEvent('rejection_chip_clicked', {
        product_name: product,
        model_family: getModelFamily(product),
        chip: chip,
        attempted_start_date: attemptedDateStr,
        reason: reason || '',
        target_date: targetDateStr || ''
      });
    },

    // 別機種チップからの着地（?tr_start）で日程引き継ぎを試みた結果。
    // status: 'applied' | disabledReason() の値（埋まっていた等）
    datePrefilled: function (dateStr, status) {
      var product = getProductName();
      sendCalendarEvent('date_prefilled', {
        product_name: product,
        model_family: getModelFamily(product),
        attempted_start_date: dateStr,
        status: status
      });
    },

    comparisonCtaClicked: function (product, targetUrl) {
      sendEvent('comparison_cta_clicked', {
        product_name: product,
        model_family: getModelFamily(product),
        target_url: targetUrl,
        page_url: window.location.pathname
      });
    },

    faqExpanded: function (question) {
      var product = getProductName();
      sendEvent('faq_expanded', {
        product_name: product,
        model_family: getModelFamily(product),
        question: question
      });
    },

    // Uncaught JS error — catches the "clicked but nothing happened" class of
    // bug where a handler throws. GA4's standard 'exception' event.
    jsError: function (description, source, errorType) {
      sendEvent('exception', {
        description: sanitizeErrorMessage(description),
        error_source: source || '',
        error_type: errorType || 'error',
        page_path: window.location.pathname,
        fatal: false
      });
    }
  };

  // ── Auto-bind: Product page CTA delegation ──
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    var product = getProductName();

    // LINE consultation link
    if (href.indexOf('lin.ee') !== -1 || href.indexOf('line.me') !== -1) {
      TRAnalytics.lineConsultClicked(product);
      return;
    }

    // Related products / alternative device clicks
    var relatedSection = link.closest('.related-products');
    if (relatedSection) {
      // The product title lives in the .card__heading (an ancestor of the
      // clicked .full-unstyled-link), so look it up from the card container.
      var card = link.closest('.card-wrapper') || relatedSection;
      var headingEl = card.querySelector('.card__heading');
      var toProduct;
      if (headingEl) {
        toProduct = headingEl.textContent.trim();
      } else if (link.matches('.full-unstyled-link')) {
        toProduct = link.textContent.trim();
      } else {
        toProduct = href;
      }
      TRAnalytics.alternativeClicked(product, toProduct);
      return;
    }

    // Comparison CTA (links to other product pages from within product page)
    if (link.closest('.product__info-container') && href.indexOf('/products/') !== -1) {
      TRAnalytics.comparisonCtaClicked(product, href);
    }
  });

  // ── Auto-bind: FAQ accordion expansion ──
  document.addEventListener('toggle', function (e) {
    if (!e.target.open) return;
    var summary = e.target.querySelector('summary');
    if (!summary) return;
    var section = e.target.closest('.collapsible-content');
    if (!section) return;
    TRAnalytics.faqExpanded(summary.textContent.trim());
  }, true);

  // ── Auto-bind: Checkout buttons ──
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[name="checkout"], .cart__checkout-button');
    if (!btn) return;

    var cartTotal = 0;
    var itemCount = 0;
    var location = 'unknown';

    var totalEl = document.querySelector('.totals__total-value');
    if (totalEl) {
      var raw = totalEl.textContent.replace(/[^0-9]/g, '');
      cartTotal = parseInt(raw, 10) || 0;
    }

    var drawer = btn.closest('cart-drawer');
    if (drawer) {
      location = 'cart_drawer';
      var drawerItems = drawer.querySelectorAll('.cart-item');
      itemCount = drawerItems.length;
    } else {
      location = 'cart_page';
      var pageItems = document.querySelectorAll('.cart-item');
      itemCount = pageItems.length;
    }

    TRAnalytics.checkoutButtonClicked(cartTotal, itemCount, location);
  });

  // ── Auto-bind: impression tracking (calendar trigger / price table) ──
  // Each element fires at most once per page load, at ≥50% visibility.
  // Intentionally once per ELEMENT, not once per page: with multiple calendar
  // triggers (inline + a future sticky CTA) each trigger_location needs its
  // own impression for per-trigger view→open attribution.
  function observeOnce(selector, onView) {
    if (!('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll(selector);
    if (!els.length) return;
    // The initial entry after observe() has isIntersecting=true even at 1px
    // visibility, so the ratio must be checked explicitly. The extra 0.75
    // threshold is a retry in case a browser reports slightly under 0.5 at
    // the crossing (rounding) — without it that element would never fire.
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        if (entries[i].intersectionRatio < 0.5) continue;
        observer.unobserve(entries[i].target);
        onView(entries[i].target);
      }
    }, { threshold: [0.5, 0.75] });
    for (var j = 0; j < els.length; j++) observer.observe(els[j]);
  }

  function bindImpressionTracking() {
    observeOnce('#rental-calendar-open, [data-calendar-trigger]', function (el) {
      TRAnalytics.calendarTriggerViewed(
        (el.dataset && el.dataset.triggerLocation) || 'inline'
      );
    });
    observeOnce('.tr-price-block', function () {
      TRAnalytics.priceTableViewed();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindImpressionTracking);
  } else {
    bindImpressionTracking();
  }

  // ── Auto-bind: uncaught JS errors ──
  // Surfaces script breakage (the "I clicked and nothing happened" class of
  // bug). Deduped by signature and capped per page so a looping error can't
  // flood GA4.
  var errorSeen = {};
  var errorCount = 0;
  var ERROR_CAP = 8;

  function reportError(description, source, errorType) {
    if (errorCount >= ERROR_CAP) return;
    var sig = errorType + '|' + description + '|' + source;
    if (errorSeen[sig]) return;
    errorSeen[sig] = true;
    errorCount++;
    TRAnalytics.jsError(description, source, errorType);
  }

  window.addEventListener('error', function (e) {
    // Ignore resource-load errors (img/script 404): no .error object and the
    // target is an element, not the window.
    if (!e.error && !e.message) return;
    var source = '';
    if (e.filename) {
      // Keep only the pathname — strip the origin and any query string / hash
      // so we never log full URLs (they can carry PII in request params).
      var path = e.filename
        .replace(/^https?:\/\/[^/]+/, '')
        .replace(/[?#].*$/, '');
      source = path + ':' + (e.lineno || 0) + ':' + (e.colno || 0);
    }
    var message = e.message || (e.error && e.error.message) || 'unknown';
    reportError(message, source, 'error');
  });

  window.addEventListener('unhandledrejection', function (e) {
    var reason = e.reason;
    var message = 'unknown';
    if (reason) {
      message = typeof reason === 'string'
        ? reason
        : (reason.message || String(reason));
    }
    reportError(message, '', 'unhandledrejection');
  });
})();
